const answerSheetContentType = {
  HEADER_IMG: 1,
  QUESTION_IMG: 2,
  ANSWER_IMG: 3,
  AUDIO_HTML: 4,
} as const
type AnswerSheetContentType = typeof answerSheetContentType[keyof typeof answerSheetContentType]

const answerViewPhase = {
  OTHER: 0,
  KOKUGO_ANSWER: 1,
  KOKUGO_SUBMIT: 2,
} as const
type AnswerViewPhase = typeof answerViewPhase[keyof typeof answerViewPhase]

/**
 * 1: 縦型配置（国語以外の配置、上から下へ配置）
 * 2: 横型配置（国語解答ページの配置、右から左へ配置）
 * 3: 集約配置（国語提出画像の配置、日本語の縦書き順序(Right to Left)の配置）
 */
const layoutPattern = {
  VERTICAL_UP_TO_DOWN: 1,
  HORIZONTAL_RIGHT_TO_LEFT: 2,
  AGGREGATE_RIGHT_TO_LEFT: 3,
} as const
type LayoutPattern = typeof layoutPattern[keyof typeof layoutPattern]

/**
 * 正整数の場合は、画像の配置順序を示す
 * -1, 0 は特殊ケースであり、下記の通り
 *
 * -1: 音声再生エリア
 * 0: 問題用紙エリア
 */
type PositionNumber = number

type ModalEvent = 'SUSPEND_STUDY' | 'CLEAR_ALL' | 'SAVE' | 'BLANK' | 'ERROR_NOTIFICATION'
type ActiveModalObject = {
  [key in ModalEvent]: boolean
}

const suspendStudyModalTexts = {
  message:
    '<ruby>解<rp>(</rp><rt>かい</rt><rp>)</rp></ruby><ruby>答<rp>(</rp><rt>とう</rt><rp>)</rp></ruby>をほぞんして、<ruby>学<rp>(</rp><rt>がく</rt><rp>)</rp></ruby><ruby>習<rp>(</rp><rt>しゅう</rt><rp>)</rp></ruby>をやめるよ。',
  whiteText:
    '<ruby>学<rp>(</rp><rt>がく</rt><rp>)</rp></ruby><ruby>習<rp>(</rp><rt>しゅう</rt><rp>)</rp></ruby>にもどる',
  blueText:
    '<ruby>学<rp>(</rp><rt>がく</rt><rp>)</rp></ruby><ruby>習<rp>(</rp><rt>しゅう</rt><rp>)</rp></ruby>をやめる',
}
const clearAllModalTexts = {
  message:
    '「黒」でかいたものが<ruby><rb>全</rb><rp>（</rp><rt>ぜん</rt><rp>）</rp><rb>部</rb><rp>（</rp><rt>ぶ</rt><rp>）</rp></ruby><ruby><rb>消</rb><rp>（</rp><rt>き</rt><rp>）</rp></ruby>えてしまうよ。<br />よかったら「<ruby><rb>全</rb><rp>（</rp><rt>ぜん</rt><rp>）</rp><rb>部</rb><rp>（</rp><rt>ぶ</rt><rp>）</rp></ruby><ruby><rb>消</rb><rp>（</rp><rt>け</rt><rp>）</rp></ruby>す」をタップしてね。',
  whiteText: '<ruby>消<rp>(</rp><rt>け</rt><rp>)</rp></ruby>さない',
  blueText:
    '<ruby>全<rp>(</rp><rt>ぜん</rt><rp>)</rp></ruby><ruby>部<rp>(</rp><rt>ぶ</rt><rp>)</rp></ruby><ruby>消<rp>(</rp><rt>け</rt><rp>)</rp></ruby>す',
}
const saveModalTexts = {
  message:
    '<ruby>解<rp>(</rp><rt>かい</rt><rp>)</rp></ruby><ruby>答<rp>(</rp><rt>とう</rt><rp>)</rp></ruby>をほぞんしたよ。',
  whiteText: 'わかった',
}
const blankErrorTexts = {
  message:
    '【エラー】<br>白紙では<ruby>提<rp>(</rp><rt>てい</rt><rp>)</rp></ruby><ruby>出<rp>(</rp><rt>しゅつ</rt><rp>)</rp></ruby>できません。<ruby>問<rp>(</rp><rt>もん</rt><rp>)</rp></ruby><ruby>題<rp>(</rp><rt>だい</rt><rp>)</rp></ruby>に<ruby>取<rp>(</rp><rt>と</rt><rp>)</rp></ruby>り組んでから、「ていしゅつにすすむ」をおしてください。<br>※おもて・うらがある場合は、<ruby>両<rp>(</rp><rt>りょう</rt><rp>)</rp></ruby><ruby>面<rp>(</rp><rt>めん</rt><rp>)</rp></ruby>に<ruby>取<rp>(</rp><rt>と</rt><rp>)</rp></ruby>り組んでください。<br>※<ruby>両<rp>(</rp><rt>りょう</rt><rp>)</rp></ruby><ruby>面<rp>(</rp><rt>めん</rt><rp>)</rp></ruby>に書きこみがあっても<ruby>提<rp>(</rp><rt>てい</rt><rp>)</rp></ruby><ruby>出<rp>(</rp><rt>しゅつ</rt><rp>)</rp></ruby>ができない場合は、「おもて・うらをきりかえる」をおしたうえで、<ruby>提<rp>(</rp><rt>てい</rt><rp>)</rp></ruby><ruby>出<rp>(</rp><rt>しゅつ</rt><rp>)</rp></ruby>してください。',
  whiteText: 'わかった',
}
const errorUserMessage =
  'エラーが<ruby>発生<rt>はっせい</rt></ruby>しました。<ruby>再度<rt>さいど</rt></ruby>お<ruby>試<rt>ため</rt></ruby>しください。'

/**
 * EL_2ND-2763: 答案データのバイナリサイズの下限値。これ未満のバイナリはデータが欠損・破損
 * している可能性が高いため、異常とみなして再生成・再提出を促す。
 * UIMはストローク量に比例するため正規データの下限が存在せず、高い閾値は書き込み量の少ない
 * 正規答案を誤検知する（本番実測: 正規データは概ね1KB以上、空UIMは66バイト、その間の帯域は
 * ほぼ出現しない）。空UIM・欠損のみを弾く値とする。
 * 答案画像は描画面積由来の自然な下限があるため大きめに設定する。
 */
const MIN_UIM_SIZE_BYTE = 100
const MIN_IMAGE_SIZE_BYTE = 10 * 1024
/** EL_2ND-2763: サイズが規定値未満だった場合にバイナリ生成を再試行する最大回数 */
const MAX_GENERATE_RETRY = 3
/** EL_2ND-2763: 同一答案でのリロードの最大回数（リロードループ防止） */
const MAX_ANSWER_RELOAD_COUNT = 2

/** EL_2ND-2763: 3回再生成してもサイズが規定値未満だった場合の、リロード前アラート */
const answerRebuildFailedReloadTexts = {
  message:
    '<ruby>答<rt>とう</rt></ruby><ruby>案<rt>あん</rt></ruby>データの<ruby>作成<rt>さくせい</rt></ruby>に<ruby>失敗<rt>しっぱい</rt></ruby>しました。<ruby>画面<rt>がめん</rt></ruby>を<ruby>読<rt>よ</rt></ruby>みこみ<ruby>直<rt>なお</rt></ruby>します。<ruby>手書<rt>てが</rt></ruby>きの<ruby>内容<rt>ないよう</rt></ruby>は<ruby>保存<rt>ほぞん</rt></ruby>されています。',
  whiteText: 'わかった',
}
/** EL_2ND-2763: リロードを繰り返しても解消しない場合の、リロードを行わないアラート */
const answerRebuildFailedGiveupTexts = {
  message:
    '<ruby>答<rt>とう</rt></ruby><ruby>案<rt>あん</rt></ruby>データの<ruby>作成<rt>さくせい</rt></ruby>に<ruby>繰<rt>く</rt></ruby>り<ruby>返<rt>かえ</rt></ruby>し<ruby>失敗<rt>しっぱい</rt></ruby>しました。おそれ<ruby>入<rt>い</rt></ruby>りますが、<ruby>前<rt>まえ</rt></ruby>の<ruby>画面<rt>がめん</rt></ruby>に<ruby>戻<rt>もど</rt></ruby>ってやり<ruby>直<rt>なお</rt></ruby>してください。',
  whiteText: 'わかった',
}

export {
  answerSheetContentType,
  AnswerSheetContentType,
  answerViewPhase,
  AnswerViewPhase,
  layoutPattern,
  LayoutPattern,
  PositionNumber,
  ModalEvent,
  ActiveModalObject,
  suspendStudyModalTexts,
  clearAllModalTexts,
  saveModalTexts,
  blankErrorTexts,
  errorUserMessage,
  MIN_UIM_SIZE_BYTE,
  MIN_IMAGE_SIZE_BYTE,
  MAX_GENERATE_RETRY,
  MAX_ANSWER_RELOAD_COUNT,
  answerRebuildFailedReloadTexts,
  answerRebuildFailedGiveupTexts,
}
